import React, { useState, useRef, useMemo, useEffect, useCallback } from 'react';
import {
  View,
  Text,
  ScrollView,
  Pressable,
  TextInput,
  useWindowDimensions,
  ActivityIndicator,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { openExternalUrl } from '@/lib/linking';
import { useLocalSearchParams, router } from 'expo-router';
import AnimatedPressable from '@/components/AnimatedPressable';
import {
  FileText,
  Clock,
  CheckCircle,
  AlertCircle,
  XCircle,
  AlertTriangle,
  ThumbsUp,
  ThumbsDown,
  TrendingUp,
  Users,
  Scale,
  Lightbulb,
  Check,
  Flag,
  ChevronRight,
  FolderSearch,
  ExternalLink,
  RefreshCw,
  HardDrive,
  FileSpreadsheet,
  FileImage,
  FileVideo,
  File,
  Sparkles,
  DollarSign,
  Edit3,
  X,
  HandCoins,
} from 'lucide-react-native';
import Animated, {
  FadeIn,
  FadeInLeft,
  FadeInRight,
  SlideInRight,
} from 'react-native-reanimated';
import { useThemeColors } from '@/lib/useThemeColors';
import { AppLayout, Breadcrumb } from '@/components/AppLayout';
import { useAlert } from '@/components/AlertDialog';
import { handleError, reportError, getUserMessage } from '@/lib/errors';
import { useInverioStore, REQUIRED_DOCUMENTS } from '@/lib/state/inverio-store';
import { useProfileStore } from '@/lib/state/profile-store';
import { useDealflowStore } from '@/lib/state/dealflow-store';
import { useStartDD, useConcludeDD, useCompletedDDReports, normalizeDDDecision } from '@/lib/dd/use-dd-reports';
import { dataRoomOpen, canSignalCommitment } from '@/lib/dealflow/stage';
import type { InvestorCommitment } from '@/lib/state/inverio-store';
import { formatCurrency } from '@/lib/utils/format';
import { getMockDataRoomDocumentsByStartup } from '@/lib/state/mock-data-provider';
import { mode } from '@/lib/config/app-mode';
import { useSubscriptionStore } from '@/lib/state/subscription-store';
import { SharedDDPanel, type AvailableDocument } from '@/components/shared-dd';
import { DDChatContainer } from '@/components/dd-chat/DDChatContainer';
import type { DDStartupContext } from '@/lib/supabase/edge-functions';
import { recordDataRoomView, getDocumentSignedUrl } from '@/lib/supabase/edge-functions';
import { useDataRoomDocuments } from '@/lib/supabase/api';
import { RequestSourcesAccessCta } from '@/components/score/RequestSourcesAccessCta';
import { listFiles as listGoogleDriveFiles } from '@/lib/google-drive/api';
import { listDropboxFolder } from '@/lib/dropbox/api';
import {
  indexDataRoomDocuments,
  googleDriveFileToIndexable,
  dropboxFileToIndexable,
} from '@/lib/dataroom/document-indexer';
import { track, useTrackScreen } from '@/lib/analytics';
import { ConclusionPanel, SoftCommitPanel, HardCommitPanel } from '@/components/investor/dd/DDPanels';
import { KeyboardAwareBody } from '@/components/modal/KeyboardAwareBody';

interface DocumentItem {
  id: string;
  name: string;
  type: string;
  mimeType: string;
  status: 'pending' | 'reviewed' | 'flagged';
  uploadDate: string;
  size: string;
  webViewLink?: string;
  provider?: 'google' | 'dropbox';
  path?: string; // For Dropbox
}

const DOC_MIME_MAP: Record<string, string> = {
  pitch_deck: 'application/pdf',
  financials: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  legal: 'application/pdf',
  product: 'application/pdf',
  team: 'application/pdf',
  market_research: 'application/pdf',
  other: 'application/pdf',
};

function mimeFromDoc(name: string, type: string): string {
  if (name.toLowerCase().endsWith('.xlsx')) return DOC_MIME_MAP.financials;
  return DOC_MIME_MAP[type] ?? 'application/pdf';
}

/** Map a real `data_room_documents` row into the DD screen's DocumentItem. */
function mapDbDocToItem(row: {
  id: string;
  name: string;
  type: string;
  status: string | null;
  file_size: string | null;
  created_at: string | null;
  metadata: unknown;
}): DocumentItem {
  const meta = (row.metadata ?? {}) as { source?: string; provider?: string; provider_file_id?: string };
  const isSynced = meta.source === 'sync';
  const provider = meta.provider === 'google-drive' ? 'google' : meta.provider === 'dropbox' ? 'dropbox' : undefined;
  // Synced Google Drive docs carry a provider file id but no stored link — build one.
  const webViewLink =
    isSynced && meta.provider === 'google-drive' && meta.provider_file_id
      ? `https://drive.google.com/file/d/${meta.provider_file_id}/view`
      : undefined;
  return {
    id: row.id,
    name: row.name,
    type: row.type,
    mimeType: mimeFromDoc(row.name, row.type),
    status: (row.status ?? 'pending') as DocumentItem['status'],
    uploadDate: row.created_at ?? '',
    size: row.file_size ?? '—',
    webViewLink,
    provider: isSynced ? provider : undefined,
  };
}

const getStatusConfig = (colors: ReturnType<typeof useThemeColors>): Record<string, { color: string; bg: string; label: string; icon: typeof Clock }> => ({
  pending: { color: colors.warning, bg: colors.amber.light, label: 'Pending', icon: Clock },
  reviewed: { color: colors.success, bg: colors.traffic.greenLight, label: 'Reviewed', icon: CheckCircle },
  flagged: { color: colors.error, bg: colors.red.light, label: 'Flagged', icon: AlertCircle },
});

// Get file type icon based on MIME type
function getFileIcon(mimeType: string, colors: ReturnType<typeof useThemeColors>) {
  if (mimeType.includes('pdf')) {
    return { icon: FileText, color: colors.error };
  }
  if (mimeType.includes('spreadsheet') || mimeType.includes('excel') || mimeType.includes('csv')) {
    return { icon: FileSpreadsheet, color: colors.success };
  }
  if (mimeType.includes('presentation') || mimeType.includes('powerpoint')) {
    return { icon: FileText, color: colors.warning };
  }
  if (mimeType.includes('image')) {
    return { icon: FileImage, color: colors.info };
  }
  if (mimeType.includes('video')) {
    return { icon: FileVideo, color: colors.secondary[500] };
  }
  if (mimeType.includes('word') || mimeType.includes('document')) {
    return { icon: FileText, color: colors.primary[600] };
  }
  return { icon: File, color: colors.text.tertiary };
}

// Infer document type from file name
function inferDocumentType(fileName: string): string {
  const lower = fileName.toLowerCase();
  if (lower.includes('pitch') || lower.includes('deck')) return 'pitch_deck';
  if (lower.includes('financial') || lower.includes('projection') || lower.includes('revenue')) return 'financials';
  if (lower.includes('cap') || lower.includes('table') || lower.includes('equity') || lower.includes('legal') || lower.includes('contract')) return 'legal';
  if (lower.includes('product') || lower.includes('roadmap')) return 'product';
  if (lower.includes('team') || lower.includes('bio') || lower.includes('founder')) return 'team';
  if (lower.includes('market') || lower.includes('research') || lower.includes('analysis')) return 'market_research';
  return 'other';
}

// Format file size
function formatFileSize(bytes?: number): string {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

// Format relative time
function formatRelativeTime(dateString?: string): string {
  if (!dateString) return '';
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays} days ago`;
  if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
  return `${Math.floor(diffDays / 30)} months ago`;
}

type DecisionType = 'invest' | 'pass' | 'follow_up';

// Breadcrumb Component
function Breadcrumbs({
  startupName,
  rightElement
}: {
  startupName: string;
  rightElement?: React.ReactNode;
}) {
  const colors = useThemeColors();
  return (
    <View className="px-4 py-3" style={{ backgroundColor: colors.surface.primary, borderBottomWidth: 1, borderBottomColor: colors.border.light }}>
      <View className="flex-row items-center gap-2 mb-2">
        <AnimatedPressable onPress={() => router.push('/(investor)/duediligence')} className="flex-row items-center gap-1.5">
          <FolderSearch size={16} color={colors.text.tertiary} />
          <Text style={{ color: colors.text.tertiary }} className="text-sm">Due Diligence</Text>
        </AnimatedPressable>
        <ChevronRight size={14} color={colors.text.tertiary} />
        <View className="flex-row items-center gap-1.5">
          <View className="w-5 h-5 rounded items-center justify-center" style={{ backgroundColor: `${colors.primary[600]}20` }}>
            <Text style={{ color: colors.primary[600] }} className="text-xs font-bold">{startupName.charAt(0)}</Text>
          </View>
          <Text style={{ color: colors.text.primary }} className="text-sm font-medium" numberOfLines={1}>{startupName}</Text>
        </View>
      </View>
      {rightElement && (
        <View className="flex-row items-center gap-2">
          {rightElement}
        </View>
      )}
    </View>
  );
}

export default function StartupDueDiligenceScreen() {
  useTrackScreen('StartupDueDiligence');
  const { id } = useLocalSearchParams<{ id: string }>();
  const { width } = useWindowDimensions();
  const colors = useThemeColors();
  const statusConfig = getStatusConfig(colors);
  // `data_room_documents.status` is a plain TEXT column (default 'pending') with
  // no enum behind it, so the three-value union above is an assumption about the
  // data, not a guarantee. An unmapped value makes this lookup `undefined` and
  // `status.label` throws, blanking the whole screen — the shape of Sentry
  // INVERIO-APP-N. Same defence the submission cards already carry.
  const statusFallback = { color: colors.text.tertiary, bg: colors.light[300], label: 'Unknown', icon: Clock };
  const isLargeScreen = width >= 768;

  const [selectedFilter, setSelectedFilter] = useState<string>('all');
  const [selectedDoc, setSelectedDoc] = useState<DocumentItem | null>(null);
  const [documents, setDocuments] = useState<DocumentItem[]>([]);
  // isLoadingDocs / docsError are DERIVED from the real-docs query below (see
  // useDataRoomDocuments) — not local state. They were previously unset useState,
  // leaving the loading spinner + error/retry UI dead and rendering "No documents
  // found" during every load and on fetch error (loading-gate / error-as-empty).
  const [mobileTab, setMobileTab] = useState<'documents' | 'chat' | 'collaborate'>('documents');
  const [desktopRightTab, setDesktopRightTab] = useState<'chat' | 'collaborate'>('chat');
  const [showConclusionPanel, setShowConclusionPanel] = useState(false);
  const [decision, setDecision] = useState<DecisionType | null>(null);
  const [finalComments, setFinalComments] = useState('');
  const [isIndexing, setIsIndexing] = useState(false);
  const [showCommitmentPanel, setShowCommitmentPanel] = useState(false);
  const [commitmentAmount, setCommitmentAmount] = useState('');
  const [commitmentNotes, setCommitmentNotes] = useState('');
  const [showHardCommitPanel, setShowHardCommitPanel] = useState(false);
  const [hardCommitAmount, setHardCommitAmount] = useState('');
  const [editingCommitment, setEditingCommitment] = useState(false);

  const scrollViewRef = useRef<ScrollView>(null);
  const { alert, success, confirm, error: showError } = useAlert();

  const dealflow = useDealflowStore((s) => s.dealflow);
  const investorProfile = useProfileStore((s) => s.investorProfile);
  const user = useProfileStore((s) => s.user);

  // Investment commitment selectors
  const commitments = useDealflowStore((s) => s.commitments);
  const addCommitment = useDealflowStore((s) => s.addCommitment);
  const updateCommitment = useDealflowStore((s) => s.updateCommitment);
  const withdrawCommitment = useDealflowStore((s) => s.withdrawCommitment);
  const convertSoftToHard = useInverioStore((s) => s.convertSoftToHard);

  // Shared DD is Premium only
  const isPremium = useSubscriptionStore((s) => s.getInvestorTier() === 'premium');

  const startup = dealflow.find(d => d.id === id);

  // Are the founder's documents open to us? The STAGE answers it: `diligence` is
  // entered only by the founder's approval, which writes the data_room_access
  // grant in the same transaction. This replaced `startup.dataRoomAccess`, a
  // second store an investor could write on their own row (retired in
  // 20260805160000). A deal at `awaiting_access` is on this screen with the
  // documents shut — that is the state the locked panels below render.
  const documentsOpen = startup ? dataRoomOpen(startup.status) : false;

  // Conclusion state from the canonical dd_reports (keyed by startup_profiles id —
  // the route `id` is the dealflow row id, so match via `startup.startup.id`).
  // `justConcludedDecision` gives immediate in-session feedback before the query
  // refetches (and is the only signal in mock/demo mode, where dd_reports is empty).
  const { data: realCompletedDDs = [] } = useCompletedDDReports(user?.id);
  const [justConcludedDecision, setJustConcludedDecision] = useState<string | null>(null);
  const [ddReportId, setDdReportId] = useState<string | null>(null);
  // The server refused to start this DD: no data-room pass for the company
  // (owner rule 2026-10-23, 20261023120000). The screen then offers the request
  // path in place of the documents, and no "Close DD" — there is no diligence
  // to close.
  const [needsDataRoom, setNeedsDataRoom] = useState<boolean>(false);
  const existingReport = useMemo(
    () => realCompletedDDs.find((r) => r.startupId === startup?.startup?.id),
    [realCompletedDDs, startup?.startup?.id],
  );
  const concludedDecision = justConcludedDecision ?? existingReport?.decision ?? null;

  // Persist the DD lifecycle to dd_reports (canonical). Start is idempotent and
  // anchors the "before" score (started_at); conclusion writes the outcome.
  const startDD = useStartDD();
  const concludeDD = useConcludeDD();
  const ddStartedFor = useRef<string | null>(null);
  useEffect(() => {
    const startupProfileId = startup?.startup?.id;
    if (!startupProfileId || !user?.id || ddStartedFor.current === startupProfileId) return;
    ddStartedFor.current = startupProfileId;
    startDD.mutate(
      {
        investorUserId: user.id,
        startupId: startupProfileId,
        startupName: startup?.startup?.company?.name ?? 'Startup',
        industry: startup?.startup?.company?.industry ?? null,
        stage: startup?.startup?.company?.stage ?? null,
      },
      {
        // Hold on to the row id: concluding is an UPDATE of this diligence
        // (conclude_dd_report keys off it), not the creation of a new one, and
        // `existingReport` only ever resolves for an ALREADY-concluded report.
        onSuccess: (data) => {
          if (data && 'needsDataRoom' in data) setNeedsDataRoom(true);
          else if (data?.id) setDdReportId(data.id);
        },
      },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startup?.startup?.id, user?.id]);

  // Active commitment for this startup (not withdrawn)
  const activeCommitment = useMemo(() =>
    commitments.find(
      c => c.startupId === id && c.investorId === investorProfile?.id && c.status !== 'withdrawn'
    ),
    [commitments, id, investorProfile?.id]
  );

  // Whether the "Signal Interest" button should show. The set of stages this
  // is offered at is a property of the spine (`canSignalCommitment` in
  // stage.ts), not a predicate this screen re-derives — see that function's
  // comment for why `awaiting_access` is included and `diligence` is not.
  const canSignalInterest = !activeCommitment && !!startup && canSignalCommitment(startup.status);

  // Open soft commit panel with pre-filled amount
  const handleOpenSoftCommit = useCallback(() => {
    // Pre-fill from the investor's STATED minimum cheque.
    //
    // This read `patterns.avgCheckSize`, which only ever existed in mock data,
    // so in production the pre-fill silently never happened. `ticketSize.min`
    // is real and persisted. Using it to seed an input the investor then edits
    // is legitimate — unlike labelling it "Avg Check" on the profile, which
    // would pass a stated preference off as observed behaviour.
    const defaultAmount = investorProfile?.ticketSize?.min;
    setCommitmentAmount(defaultAmount ? String(defaultAmount / 1000) : '');
    setCommitmentNotes('');
    setShowCommitmentPanel(true);
  }, [investorProfile?.ticketSize?.min]);

  // Submit soft commitment
  const handleSubmitSoftCommit = useCallback(() => {
    try {
      const amountNum = parseFloat(commitmentAmount);
      if (!amountNum || amountNum <= 0) {
        alert('Invalid Amount', 'Please enter a valid commitment amount.');
        return;
      }
      if (!investorProfile || !user) {
        alert('Missing Profile', 'Investor profile is required.');
        return;
      }

      const amountInDollars = amountNum * 1000; // Input is in thousands

      const commitment: InvestorCommitment = {
        id: `commit-${Date.now()}`,
        investorId: investorProfile.id,
        investorName: user.name,
        investorFirm: investorProfile.firm,
        startupId: id!,
        type: 'soft',
        source: 'verified',
        amount: amountInDollars,
        status: 'active',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        notes: commitmentNotes.trim() || undefined,
      };

      addCommitment(commitment);
      setShowCommitmentPanel(false);
      success('Interest Signaled', `Soft commitment of ${formatCurrency(amountInDollars)} recorded.`);
      track('commitment_created', { type: 'soft', amount: amountInDollars, startupId: id });
    } catch (err) {
      const { message, isCancelled } = handleError(err, { context: 'DDScreen.handleSubmitSoftCommit' });
      if (!isCancelled) showError('Commitment Failed', message);
    }
  }, [commitmentAmount, commitmentNotes, investorProfile, user, id, addCommitment, success, alert, showError]);

  // Update existing commitment amount
  const handleUpdateCommitment = useCallback((newAmountStr: string) => {
    try {
      const amountNum = parseFloat(newAmountStr);
      if (!amountNum || amountNum <= 0 || !activeCommitment) return;

      const amountInDollars = amountNum * 1000;
      updateCommitment(activeCommitment.id, { amount: amountInDollars });
      setEditingCommitment(false);
      success('Commitment Updated', `Amount updated to ${formatCurrency(amountInDollars)}.`);
      track('commitment_updated', { amount: amountInDollars, startupId: id });
    } catch (err) {
      const { message, isCancelled } = handleError(err, { context: 'DDScreen.handleUpdateCommitment' });
      if (!isCancelled) showError('Update Failed', message);
    }
  }, [activeCommitment, updateCommitment, success, showError, id]);

  // Withdraw commitment
  const handleWithdrawCommitment = useCallback(async () => {
    if (!activeCommitment) return;
    const confirmed = await confirm(
      'Withdraw Commitment',
      `Are you sure you want to withdraw your ${formatCurrency(activeCommitment.amount)} commitment?`
    );
    if (confirmed) {
      withdrawCommitment(activeCommitment.id);
      success('Commitment Withdrawn', 'Your commitment has been withdrawn.');
      track('commitment_withdrawn', { startupId: id });
    }
  }, [activeCommitment, confirm, withdrawCommitment, success, id]);

  // Fetch data room files on mount
  const fetchDataRoomFiles = useCallback(async () => {
    if (!startup) return;

    if (!documentsOpen) {
      // No data room connected - show empty state
      setDocuments([]);
      return;
    }

    // In mock mode, populate from mock data room documents
    if (mode.showSampleData) {
      const startupId = startup.startup?.id;
      const mockDocs = startupId ? getMockDataRoomDocumentsByStartup(startupId) : undefined;
      if (mockDocs?.length) {
        const mimeMap: Record<string, string> = {
          pitch_deck: 'application/pdf',
          financials: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
          legal: 'application/pdf',
          product: 'application/pdf',
          team: 'application/pdf',
          market_research: 'application/pdf',
          other: 'application/pdf',
        };
        setDocuments(mockDocs.map(d => ({
          id: d.id,
          name: d.name,
          type: d.type,
          mimeType: d.name.endsWith('.xlsx') ? mimeMap.financials : mimeMap[d.type] ?? 'application/pdf',
          status: d.status,
          uploadDate: d.uploadDate,
          size: d.size,
        })));
        return;
      }
    }

    // Data room access granted but provider/folder details come from
    // the connected data room integration (not pitch deck extraction).
    // TODO: Wire up data room provider info when integration is available.
    setDocuments([]);
  }, [startup, documentsOpen]);

  useEffect(() => {
    fetchDataRoomFiles();
  }, [fetchDataRoomFiles]);

  // Real documents (production): a granted investor reads data_room_documents
  // via RLS. This overrides fetchDataRoomFiles' empty prod fallback so the
  // investor sees the founder's actual data room, and opening a doc records a
  // real view event (FR-P0-07).
  const realDocsStartupId =
    !mode.showSampleData && documentsOpen ? startup?.startup?.id : undefined;
  const {
    data: realDocRows,
    isLoading: realDocsLoading,
    error: realDocsError,
    refetch: refetchRealDocs,
  } = useDataRoomDocuments(realDocsStartupId);
  useEffect(() => {
    if (mode.showSampleData || !realDocsStartupId) return;
    setDocuments((realDocRows ?? []).map(mapDbDocToItem));
  }, [realDocRows, realDocsStartupId]);

  // Derived loading/error for the data-room doc panels. Only meaningful when we
  // are actually fetching real docs (granted access, prod mode); in sample mode /
  // no-access the panels use the synchronous fetchDataRoomFiles path.
  const isLoadingDocs = !!realDocsStartupId && realDocsLoading;
  const docsError = realDocsStartupId && realDocsError ? getUserMessage(realDocsError) : null;
  const retryLoadDocs = useCallback(() => {
    refetchRealDocs();
    fetchDataRoomFiles();
  }, [refetchRealDocs, fetchDataRoomFiles]);

  // Build startup context for DD Chat
  const startupContext: DDStartupContext = useMemo(() => {
    if (!startup) return { name: 'Unknown' };

    const s = startup.startup;
    const mrrMetric = s.traction?.metrics?.find(m => m.name === 'MRR');
    const usersMetric = s.traction?.metrics?.find(m => m.name === 'Users');
    const growthMetric = s.traction?.metrics?.find(m => m.name === 'Growth');
    return {
      name: s.company?.name ?? 'Unknown',
      stage: s.company?.stage,
      industry: s.company?.industry,
      location: s.company?.location,
      foundedYear: s.company?.founded,
      website: s.company?.website,
      teamSize: s.team?.size,
      tagline: s.company?.tagline,
      description: s.company?.description,
      problem: s.problemSolution?.problem,
      solution: s.problemSolution?.solution,
      // Missing metrics stay undefined — a `?? 0` here would hand the DD Chat
      // LLM a fabricated "$0 MRR / $0 target" as fact (all fields optional).
      monthlyRevenue: mrrMetric?.value,
      userCount: usersMetric?.value,
      growthRate: growthMetric?.value,
      marketSize: typeof s.market?.tam === 'number' ? `$${(s.market.tam / 1000000).toFixed(0)}M` : s.market?.tam,
      competitors: Array.isArray(s.market?.competitors) ? s.market.competitors.map(c => c.name).join(', ') : undefined,
      fundingTarget: s.funding?.currentRaise?.amount,
      isActivelyRaising: s.funding?.isActivelyRaising,
      dataRoomConnected: documentsOpen,
    };
  }, [startup, documentsOpen]);

  // Dynamic breadcrumbs based on startup name
  const breadcrumbs: Breadcrumb[] = useMemo(() => [
    { label: 'Due Diligence', href: '/(investor)/duediligence' },
    { label: startup?.startup.company?.name || 'Startup' },
  ], [startup?.startup.company?.name]);

  const missingDocuments = useMemo(() => {
    const uploadedTypes = new Set(documents.map(d => d.type));
    return REQUIRED_DOCUMENTS.filter(req => !uploadedTypes.has(req.type));
  }, [documents]);

  // Convert documents to AvailableDocument format for note linking
  const availableDocuments: AvailableDocument[] = useMemo(() =>
    documents.map(doc => ({
      id: doc.id,
      name: doc.name,
      type: doc.type,
    })),
    [documents]
  );

  const filters = ['all', 'pending', 'reviewed', 'flagged'];
  const filteredDocs = selectedFilter === 'all'
    ? documents
    : documents.filter(d => d.status === selectedFilter);

  const stats = {
    total: documents.length,
    pending: documents.filter(d => d.status === 'pending').length,
    reviewed: documents.filter(d => d.status === 'reviewed').length,
    flagged: documents.filter(d => d.status === 'flagged').length,
  };

  const updateDocStatus = (docId: string, status: 'pending' | 'reviewed' | 'flagged') => {
    setDocuments(prev => prev.map(d => d.id === docId ? { ...d, status } : d));
  };

  /**
   * Handle document press from shared DD notes
   * Opens/selects the document and marks it as reviewed
   */
  const handleDocumentPress = (documentId: string, documentName: string) => {
    // Find the document
    const doc = documents.find(d => d.id === documentId);
    if (!doc) return;

    // Select the document (shows it in the UI)
    setSelectedDoc(doc);

    // Mark as reviewed if not already
    if (doc.status === 'pending') {
      updateDocStatus(documentId, 'reviewed');
    }

    // On mobile, switch to documents tab to show the selection
    if (!isLargeScreen) {
      setMobileTab('documents');
    }
  };

  // Open document in browser/viewer
  const handleOpenDocument = useCallback(async (doc: DocumentItem) => {
    track('document_viewed', { documentId: doc.id, type: doc.type });
    // Real view telemetry — fire-and-forget; the server no-ops for owner
    // self-views and mock / non-existent documents (FR-P0-07).
    recordDataRoomView(doc.id).catch((err) =>
      reportError(err, { context: 'DueDiligence.recordView', level: 'warning', silent: true }),
    );

    if (doc.webViewLink) {
      track('document_downloaded', { documentId: doc.id, type: doc.type });
      openExternalUrl(doc.webViewLink, 'DueDiligence.openDoc');
      return;
    }
    if (doc.provider === 'dropbox' && doc.path) {
      track('document_downloaded', { documentId: doc.id, type: doc.type });
      openExternalUrl(`https://www.dropbox.com/home${doc.path}`, 'DueDiligence.openDoc');
      return;
    }

    // Bucket-backed doc: fetch a short-lived signed URL and open it.
    const { data, error: urlError } = await getDocumentSignedUrl({ documentId: doc.id });
    if (urlError || !data?.url) {
      const { message, isCancelled } = handleError(
        urlError ? new Error(urlError) : new Error('No signed URL returned'),
        { context: 'DueDiligence.openDoc' },
      );
      if (!isCancelled) alert('Cannot open document', message);
      return;
    }
    track('document_downloaded', { documentId: doc.id, type: doc.type });
    openExternalUrl(data.url, 'DueDiligence.openDoc');
  }, [alert]);

  /**
   * Write the conclusion. `conclude_dd_report` does the whole move in one
   * transaction — marks the report concluded, sets the dealflow stage
   * (invest → invested, pass → rejected, monitor → monitoring) and creates the
   * portfolio holding — so this is the single write, and the terminal UI runs
   * only after it succeeds, never optimistically.
   *
   * `amount` is the real committed figure on an invest conclusion (the holding
   * is built from it) and null otherwise.
   */
  const submitConclusion = useCallback(
    (amount: number | null, onConcluded: (reportId: string | null) => void) => {
      if (!decision || !finalComments.trim() || !user?.id) return;

      // Keyed by the startup_profiles id (the route `id` is the dealflow item
      // id). Without it there is nothing to conclude, so fail honestly rather
      // than show a "closed" toast for a write that never happened.
      const startupProfileId = startup?.startup?.id;
      if (!startupProfileId) {
        showError('Cannot Conclude', 'This due-diligence session is still loading. Please try again in a moment.');
        return;
      }

      concludeDD.mutate(
        {
          reportId: ddReportId ?? existingReport?.id ?? null,
          investorUserId: user.id,
          startupId: startupProfileId,
          decision: normalizeDDDecision(decision),
          amount,
          notes: finalComments.trim(),
        },
        {
          onSuccess: (data) => {
            setJustConcludedDecision(normalizeDDDecision(decision));
            if (data?.id) setDdReportId(data.id);
            setShowConclusionPanel(false);
            onConcluded(data?.id ?? null);
          },
          onError: (err) => {
            const { message, isCancelled } = handleError(err, { context: 'DueDiligence.concludeDD' });
            if (!isCancelled) showError('Could Not Save Conclusion', message);
          },
        },
      );
    },
    [decision, finalComments, user?.id, startup?.startup?.id, ddReportId, existingReport?.id, concludeDD, showError],
  );

  const handleSubmitConclusion = useCallback(() => {
    if (!decision || !finalComments.trim() || !investorProfile || !user) {
      alert('Missing Information', 'Please select a decision and add your comments.');
      return;
    }

    if (decision !== 'invest') {
      submitConclusion(null, () => success('Due Diligence Closed', 'Your conclusion has been submitted.'));
      return;
    }

    // An invest conclusion creates a portfolio holding, and a holding needs a
    // real figure — conclude_dd_report refuses an invest with no amount rather
    // than writing a zero that would later read as a genuine $0 investment. So
    // the amount is SETTLED FIRST and the conclusion carries it; it used to be
    // collected after the write, which is why the write had none to carry.
    //   * a hard commitment already IS that figure — conclude straight away
    //   * a soft one is confirmed (and converted) in the commit panel
    //   * with none, we ask; the field is pre-filled from the stated minimum
    //     cheque, but the investor still confirms it — nothing is invented.
    if (activeCommitment?.type === 'hard') {
      submitConclusion(activeCommitment.amount, () =>
        success('Due Diligence Closed', 'Your invest decision has been submitted.'),
      );
      return;
    }

    const seed = activeCommitment?.amount ?? investorProfile?.ticketSize?.min;
    setHardCommitAmount(seed ? String(seed / 1000) : '');
    setShowConclusionPanel(false);
    setShowHardCommitPanel(true);
  }, [decision, finalComments, investorProfile, user, activeCommitment, submitConclusion, alert, success]);

  // Submit hard commitment (from DD close invest flow). This panel is only ever
  // opened by an invest conclusion that still needs its amount, so the DD
  // conclusion is written HERE, first: it is what moves the deal to `invested`
  // and creates the portfolio holding, and a local commitment record must never
  // claim an investment the conclusion failed to record.
  const handleSubmitHardCommit = useCallback(() => {
    const amountNum = parseFloat(hardCommitAmount);
    if (!amountNum || amountNum <= 0) {
      alert('Invalid Amount', 'Please enter a valid commitment amount.');
      return;
    }
    if (!investorProfile || !user) return;

    const amountInDollars = amountNum * 1000;

    submitConclusion(amountInDollars, (reportId) => {
      try {
        // Link the commitment to the canonical dd_reports row (the id the
        // conclusion just returned, else the one we already held). Replaces the
        // legacy ephemeral conclusion id.
        const conclusionRef = reportId ?? ddReportId ?? existingReport?.id;

        if (activeCommitment && activeCommitment.type === 'soft') {
          // Convert existing soft to hard
          convertSoftToHard(activeCommitment.id, amountInDollars, conclusionRef);
          success('Commitment Converted', `Hard commitment of ${formatCurrency(amountInDollars)} confirmed.`);
          track('commitment_converted', { type: 'hard', amount: amountInDollars, startupId: id });
        } else {
          // Create new hard commitment
          const commitment: InvestorCommitment = {
            id: `commit-${Date.now()}`,
            investorId: investorProfile.id,
            investorName: user.name,
            investorFirm: investorProfile.firm,
            startupId: id!,
            type: 'hard',
            source: 'verified',
            amount: amountInDollars,
            status: 'active',
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
            conclusionId: conclusionRef,
          };
          addCommitment(commitment);
          success('Hard Commitment', `Investment commitment of ${formatCurrency(amountInDollars)} confirmed.`);
          track('commitment_created', { type: 'hard', amount: amountInDollars, startupId: id });
        }

        setShowHardCommitPanel(false);
      } catch (err) {
        const { message, isCancelled } = handleError(err, { context: 'DDScreen.handleSubmitHardCommit' });
        if (!isCancelled) showError('Commitment Failed', message);
      }
    });
  }, [hardCommitAmount, investorProfile, user, id, activeCommitment, ddReportId, existingReport?.id, convertSoftToHard, addCommitment, submitConclusion, success, alert, showError]);

  if (!startup) {
    return (
      <AppLayout title="Due Diligence" breadcrumbs={breadcrumbs}>
        <View className="flex-1 items-center justify-center" style={{ backgroundColor: colors.surface.secondary }}>
          <Text style={{ color: colors.text.secondary }}>Startup not found</Text>
          <Pressable onPress={() => router.back()} className="mt-4 px-4 py-2 rounded-lg" style={{ backgroundColor: colors.primary[600] }}>
            <Text className="text-white">Go Back</Text>
          </Pressable>
        </View>
      </AppLayout>
    );
  }

  // Commitment badge for breadcrumb row
  const commitmentBadge = activeCommitment ? (
    <View className="flex-row items-center gap-1.5">
      {!editingCommitment ? (
        <View className="flex-row items-center gap-1.5 px-3 py-1.5 rounded-lg" style={{ backgroundColor: activeCommitment.type === 'hard' ? colors.traffic.greenLight : `${colors.primary[600]}15` }}>
          <DollarSign size={14} color={activeCommitment.type === 'hard' ? colors.success : colors.primary[600]} />
          <Text className="text-sm font-semibold" style={{ color: activeCommitment.type === 'hard' ? colors.success : colors.primary[600] }}>
            {formatCurrency(activeCommitment.amount)}
          </Text>
          <Text className="text-xs" style={{ color: colors.text.tertiary }}>
            {activeCommitment.type === 'soft' ? 'soft' : 'committed'}
          </Text>
          <Pressable onPress={() => { setCommitmentAmount(String(activeCommitment.amount / 1000)); setEditingCommitment(true); }} className="ml-1 active:opacity-70">
            <Edit3 size={13} color={colors.text.tertiary} />
          </Pressable>
          <Pressable onPress={handleWithdrawCommitment} className="ml-0.5 active:opacity-70">
            <X size={13} color={colors.text.tertiary} />
          </Pressable>
        </View>
      ) : (
        <View className="flex-row items-center gap-1.5">
          <Text className="text-sm" style={{ color: colors.text.secondary }}>$</Text>
          <TextInput
            className="px-2 py-1 rounded-md text-sm w-20"
            style={{ backgroundColor: colors.surface.secondary, color: colors.text.primary, borderWidth: 1, borderColor: colors.primary[600] }}
            value={commitmentAmount}
            onChangeText={setCommitmentAmount}
            keyboardType="numeric"
            placeholder="Amount"
            placeholderTextColor={colors.text.tertiary}
            autoFocus
          />
          <Text className="text-xs" style={{ color: colors.text.tertiary }}>K</Text>
          <Pressable onPress={() => handleUpdateCommitment(commitmentAmount)} className="px-2 py-1 rounded-md active:opacity-70" style={{ backgroundColor: colors.primary[600] }}>
            <Check size={14} color="white" />
          </Pressable>
          <Pressable onPress={() => setEditingCommitment(false)} className="px-1 py-1 active:opacity-70">
            <X size={14} color={colors.text.tertiary} />
          </Pressable>
        </View>
      )}
    </View>
  ) : canSignalInterest ? (
    <AnimatedPressable
      onPress={handleOpenSoftCommit}
      className="flex-row items-center gap-1.5 px-3 py-2 rounded-lg"
      style={{ backgroundColor: `${colors.primary[600]}15`, borderWidth: 1, borderColor: `${colors.primary[600]}40` }}
    >
      <HandCoins size={15} color={colors.primary[600]} />
      <Text className="text-sm font-medium" style={{ color: colors.primary[600] }}>Signal Interest</Text>
    </AnimatedPressable>
  ) : null;

  // Close DD button for breadcrumb row
  const closeDDButton = needsDataRoom ? null : !concludedDecision ? (
    <AnimatedPressable
      onPress={() => setShowConclusionPanel(true)}
      className="flex-row items-center gap-2 px-4 py-2 rounded-lg"
      style={{ backgroundColor: colors.primary[600] }}
    >
      <CheckCircle size={16} color="white" />
      <Text className="text-white text-sm font-medium">Close DD</Text>
    </AnimatedPressable>
  ) : (
    <View className="flex-row items-center gap-2 px-3 py-1.5 rounded-lg" style={{ backgroundColor: concludedDecision === 'invest' ? colors.traffic.greenLight : concludedDecision === 'pass' ? colors.red.light : colors.amber.light }}>
      {concludedDecision === 'invest' && <ThumbsUp size={14} color={colors.success} />}
      {concludedDecision === 'pass' && <ThumbsDown size={14} color={colors.error} />}
      {/* `monitor` is the spine's terminal "keep watching" stage; `follow`/`follow_up` are its legacy spellings, still on rows written before the enum was collapsed. */}
      {(concludedDecision === 'monitor' || concludedDecision === 'follow' || concludedDecision === 'follow_up') && <Clock size={14} color={colors.warning} />}
      <Text className="text-sm font-medium" style={{ color: concludedDecision === 'invest' ? colors.success : concludedDecision === 'pass' ? colors.error : colors.warning }}>
        {concludedDecision === 'invest' ? 'Investing' : concludedDecision === 'pass' ? 'Passed' : 'Following Up'}
      </Text>
    </View>
  );

  // Combined right element: commitment badge + close DD button
  const breadcrumbRightElement = (
    <View className="flex-row items-center gap-2">
      {commitmentBadge}
      {closeDDButton}
    </View>
  );

  // Mobile Layout
  if (!isLargeScreen) {
    return (
      <AppLayout title="Due Diligence" breadcrumbs={breadcrumbs}>
        <SafeAreaView edges={['top']} className="flex-1" style={{ backgroundColor: colors.surface.secondary }}>
          {/* Breadcrumbs with Close DD button */}
          <Breadcrumbs startupName={startup.startup.company?.name} rightElement={breadcrumbRightElement} />

          {/* Tab Switcher for Mobile */}
          <View className="flex-row" style={{ backgroundColor: colors.surface.primary }}>
            <Pressable
              onPress={() => setMobileTab('documents')}
              className="flex-1 py-3 items-center"
              style={{ borderBottomWidth: 2, borderBottomColor: mobileTab === 'documents' ? colors.primary[600] : 'transparent' }}
            >
              <Text style={{ color: mobileTab === 'documents' ? colors.primary[600] : colors.text.tertiary }} className="font-medium">Documents</Text>
            </Pressable>
            <Pressable
              onPress={() => setMobileTab('chat')}
              className="flex-1 py-3 items-center"
              style={{ borderBottomWidth: 2, borderBottomColor: mobileTab === 'chat' ? colors.primary[600] : 'transparent' }}
            >
              <Text style={{ color: mobileTab === 'chat' ? colors.primary[600] : colors.text.tertiary }} className="font-medium">AI Chat</Text>
            </Pressable>
            <Pressable
              onPress={() => setMobileTab('collaborate')}
              className="flex-1 py-3 items-center"
              style={{ borderBottomWidth: 2, borderBottomColor: mobileTab === 'collaborate' ? colors.primary[600] : 'transparent' }}
            >
              <Text style={{ color: mobileTab === 'collaborate' ? colors.primary[600] : colors.text.tertiary }} className="font-medium">Collaborate</Text>
            </Pressable>
          </View>

          {mobileTab === 'documents' ? (
            // Documents List
            <ScrollView className="flex-1 p-4">
              {/* Stats */}
              <View className="flex-row gap-2 mb-4">
                {filters.map(f => (
                  <AnimatedPressable
                    key={f}
                    onPress={() => setSelectedFilter(f)}
                    className="px-3 py-1.5 rounded-full"
                    style={{ backgroundColor: selectedFilter === f ? colors.primary[600] : colors.surface.primary }}
                  >
                    <Text className="text-xs font-medium capitalize" style={{ color: selectedFilter === f ? 'white' : colors.text.secondary }}>
                      {f} {f !== 'all' && `(${stats[f as keyof typeof stats]})`}
                    </Text>
                  </AnimatedPressable>
                ))}
              </View>

              {/* No pass: the existing request path, not a dead end. */}
              {needsDataRoom && startup?.startup?.id ? (
                <View className="mb-4">
                  <RequestSourcesAccessCta
                    startupId={startup.startup.id}
                    dealId={startup.id}
                    startupName={startup.startup.company?.name ?? 'this startup'}
                    purpose="diligence"
                  />
                </View>
              ) : null}

              {/* Data Room Connection Status */}
              {!documentsOpen && !needsDataRoom && (
                <View className="rounded-xl p-4 mb-4" style={{ backgroundColor: colors.surface.primary }}>
                  <View className="flex-row items-center gap-3 mb-2">
                    <HardDrive size={20} color={colors.text.tertiary} />
                    <Text style={{ color: colors.text.primary }} className="font-medium">No Data Room Connected</Text>
                  </View>
                  <Text style={{ color: colors.text.tertiary }} className="text-sm">
                    The startup hasn't connected their data room yet.
                  </Text>
                </View>
              )}

              {/* Loading State */}
              {isLoadingDocs && (
                <View className="items-center justify-center py-8">
                  <ActivityIndicator color={colors.primary[600]} />
                  <Text style={{ color: colors.text.tertiary }} className="mt-2 text-sm">Loading data room files...</Text>
                </View>
              )}

              {/* Error State */}
              {docsError && (
                <View className="rounded-xl p-4 mb-4" style={{ backgroundColor: colors.red.light }}>
                  <View className="flex-row items-center gap-2">
                    <AlertCircle size={16} color={colors.error} />
                    <Text style={{ color: colors.error }} className="text-sm">{docsError}</Text>
                  </View>
                  <Pressable onPress={retryLoadDocs} className="flex-row items-center gap-1 mt-2">
                    <RefreshCw size={14} color={colors.primary[600]} />
                    <Text style={{ color: colors.primary[600] }} className="text-sm">Retry</Text>
                  </Pressable>
                </View>
              )}

              {/* Indexing indicator */}
              {isIndexing && (
                <View className="flex-row items-center gap-2 mb-4 px-3 py-2 rounded-lg" style={{ backgroundColor: `${colors.primary[600]}15` }}>
                  <ActivityIndicator size="small" color={colors.primary[600]} />
                  <Text style={{ color: colors.primary[600] }} className="text-xs">Indexing documents for AI...</Text>
                </View>
              )}

              {/* Missing Docs Warning */}
              {missingDocuments.length > 0 && documents.length > 0 && (
                <View className="rounded-xl p-3 mb-4" style={{ backgroundColor: colors.amber.light }}>
                  <View className="flex-row items-center gap-2">
                    <AlertTriangle size={16} color={colors.warning} />
                    <Text style={{ color: colors.warning }} className="font-medium text-sm">{missingDocuments.length} missing documents</Text>
                  </View>
                </View>
              )}

              {/* Documents */}
              {filteredDocs.map((doc, index) => {
                const status = statusConfig[doc.status] ?? statusFallback;
                const { icon: TypeIcon, color: typeColor } = getFileIcon(doc.mimeType, colors);

                return (
                  <Animated.View key={doc.id} entering={FadeIn.delay(index * 50)}>
                    <View className="rounded-xl p-4 mb-3" style={{ backgroundColor: colors.surface.primary }}>
                      <View className="flex-row items-center gap-3">
                        <View className="w-10 h-10 rounded-lg items-center justify-center" style={{ backgroundColor: `${typeColor}15` }}>
                          <TypeIcon size={20} color={typeColor} />
                        </View>
                        <View className="flex-1">
                          <Text style={{ color: colors.text.primary }} className="font-medium" numberOfLines={1}>{doc.name}</Text>
                          <Text style={{ color: colors.text.tertiary }} className="text-xs">{doc.size} • {doc.uploadDate}</Text>
                        </View>
                        <View className="px-2 py-1 rounded-full" style={{ backgroundColor: status.bg }}>
                          <Text style={{ color: status.color }} className="text-xs font-medium">{status.label}</Text>
                        </View>
                      </View>
                      <View className="flex-row gap-2 mt-3">
                        <AnimatedPressable onPress={() => handleOpenDocument(doc)} className="flex-1 flex-row items-center justify-center gap-1 py-2 rounded-lg" style={{ backgroundColor: colors.surface.secondary }}>
                          <ExternalLink size={14} color={colors.text.secondary} />
                          <Text style={{ color: colors.text.secondary }} className="text-xs font-medium">Open</Text>
                        </AnimatedPressable>
                        <AnimatedPressable onPress={() => updateDocStatus(doc.id, 'reviewed')} className="flex-1 flex-row items-center justify-center gap-1 py-2 rounded-lg" style={{ backgroundColor: colors.traffic.greenLight }}>
                          <Check size={14} color={colors.success} />
                          <Text style={{ color: colors.success }} className="text-xs font-medium">Reviewed</Text>
                        </AnimatedPressable>
                        <AnimatedPressable onPress={() => updateDocStatus(doc.id, 'flagged')} className="flex-1 flex-row items-center justify-center gap-1 py-2 rounded-lg" style={{ backgroundColor: colors.red.light }}>
                          <Flag size={14} color={colors.error} />
                          <Text style={{ color: colors.error }} className="text-xs font-medium">Flag</Text>
                        </AnimatedPressable>
                      </View>
                    </View>
                  </Animated.View>
                );
              })}

              {/* Empty State */}
              {!isLoadingDocs && !docsError && documents.length === 0 && documentsOpen && (
                <View className="items-center justify-center py-8">
                  <FileText size={40} color={colors.text.tertiary} />
                  <Text style={{ color: colors.text.secondary }} className="mt-2 font-medium">No documents found</Text>
                  <Text style={{ color: colors.text.tertiary }} className="text-sm text-center mt-1">
                    The connected data room folder is empty.
                  </Text>
                </View>
              )}
            </ScrollView>
          ) : mobileTab === 'chat' ? (
            // AI Chat - Real DDChatContainer
            <View className="flex-1">
              <DDChatContainer
                startupContext={startupContext}
                startupId={startup.startup.id}
                flexLayout={true}
                investorPreferences={{
                  focusAreas: investorProfile?.focusAreas,
                  riskTolerance: investorProfile?.riskTolerance,
                  investmentStage: investorProfile?.investmentStages,
                }}
              />
            </View>
          ) : (
            // Collaborate Tab
            <ScrollView className="flex-1 p-4">
              <SharedDDPanel
                startupId={startup.id}
                startupName={startup.startup.company?.name}
                investorUserId={user?.id}
                investorName={user?.name || 'Investor'}
                isPremium={isPremium}
                availableDocuments={availableDocuments}
                onDocumentPress={handleDocumentPress}
              />
            </ScrollView>
          )}

          <ConclusionPanel
            visible={showConclusionPanel}
            onClose={() => setShowConclusionPanel(false)}
            decision={decision}
            onSetDecision={setDecision}
            finalComments={finalComments}
            onSetFinalComments={setFinalComments}
            onSubmit={handleSubmitConclusion}
          />

          <SoftCommitPanel
            visible={showCommitmentPanel}
            onClose={() => setShowCommitmentPanel(false)}
            startupName={startup.startup.company?.name}
            commitmentAmount={commitmentAmount}
            onSetCommitmentAmount={setCommitmentAmount}
            commitmentNotes={commitmentNotes}
            onSetCommitmentNotes={setCommitmentNotes}
            onSubmit={handleSubmitSoftCommit}
          />

          <HardCommitPanel
            visible={showHardCommitPanel}
            onClose={() => setShowHardCommitPanel(false)}
            hardCommitAmount={hardCommitAmount}
            onSetHardCommitAmount={setHardCommitAmount}
            onSubmit={handleSubmitHardCommit}
            isConversion={activeCommitment?.type === 'soft'}
            previousAmount={activeCommitment?.type === 'soft' ? activeCommitment.amount : undefined}
          />
        </SafeAreaView>
      </AppLayout>
    );
  }

  // Desktop/Tablet Layout - NotebookLM Style (within AppLayout)
  return (
    <AppLayout title="Due Diligence" constrainWidth={false} breadcrumbs={breadcrumbs}>
      <View className="flex-1" style={{ backgroundColor: colors.surface.secondary }}>
        {/* Breadcrumbs with Close DD button */}
        <Breadcrumbs startupName={startup.startup.company?.name} rightElement={breadcrumbRightElement} />

        {/* Main Content - Split View */}
        <View className="flex-1 flex-row">
          {/* Left Panel - Documents */}
          <Animated.View
            entering={FadeInLeft.delay(100).duration(400)}
            className="w-[380px] border-r"
            style={{ backgroundColor: colors.surface.primary, borderRightColor: colors.border.light }}
          >
            {/* Document Header */}
            <View className="p-4 border-b" style={{ borderBottomColor: colors.border.light }}>
              <View className="flex-row items-center justify-between mb-3">
                <Text style={{ color: colors.text.primary }} className="text-lg font-semibold">Data Room</Text>
                <View className="flex-row items-center gap-2">
                  <View className="px-2 py-1 rounded-full" style={{ backgroundColor: colors.traffic.greenLight }}>
                    <Text style={{ color: colors.success }} className="text-xs font-medium">{stats.reviewed} reviewed</Text>
                  </View>
                </View>
              </View>

              {/* Filters */}
              <ScrollView horizontal showsHorizontalScrollIndicator={false} style={{ flexGrow: 0 }}>
                <View className="flex-row gap-2">
                  {filters.map(f => (
                    <AnimatedPressable
                      key={f}
                      onPress={() => setSelectedFilter(f)}
                      className="px-3 py-1.5 rounded-full"
                      style={{ backgroundColor: selectedFilter === f ? colors.primary[600] : colors.surface.secondary }}
                    >
                      <Text className="text-xs font-medium capitalize" style={{ color: selectedFilter === f ? 'white' : colors.text.secondary }}>
                        {f}
                      </Text>
                    </AnimatedPressable>
                  ))}
                </View>
              </ScrollView>
            </View>

            {/* Data Room Connection Status */}
            {needsDataRoom && startup?.startup?.id ? (
              <View className="mx-4 mt-1">
                <RequestSourcesAccessCta
                  startupId={startup.startup.id}
                  dealId={startup.id}
                  startupName={startup.startup.company?.name ?? 'this startup'}
                  purpose="diligence"
                />
              </View>
            ) : null}

            {!documentsOpen && !needsDataRoom && (
              <View className="mx-4 mt-4 p-4 rounded-xl" style={{ backgroundColor: colors.surface.secondary }}>
                <View className="flex-row items-center gap-3 mb-2">
                  <HardDrive size={20} color={colors.text.tertiary} />
                  <Text style={{ color: colors.text.primary }} className="font-medium">No Data Room Connected</Text>
                </View>
                <Text style={{ color: colors.text.tertiary }} className="text-sm">
                  The startup hasn't connected their data room yet.
                </Text>
              </View>
            )}

            {/* Loading State */}
            {isLoadingDocs && (
              <View className="items-center justify-center py-8">
                <ActivityIndicator color={colors.primary[600]} />
                <Text style={{ color: colors.text.tertiary }} className="mt-2 text-sm">Loading data room files...</Text>
              </View>
            )}

            {/* Error State */}
            {docsError && (
              <View className="mx-4 mt-4 p-3 rounded-xl" style={{ backgroundColor: colors.red.light }}>
                <View className="flex-row items-center gap-2">
                  <AlertCircle size={16} color={colors.error} />
                  <Text style={{ color: colors.error }} className="text-sm">{docsError}</Text>
                </View>
                <Pressable onPress={retryLoadDocs} className="flex-row items-center gap-1 mt-2">
                  <RefreshCw size={14} color={colors.primary[600]} />
                  <Text style={{ color: colors.primary[600] }} className="text-sm">Retry</Text>
                </Pressable>
              </View>
            )}

            {/* Indexing Indicator */}
            {isIndexing && (
              <View className="mx-4 mt-4 flex-row items-center gap-2 px-3 py-2 rounded-lg" style={{ backgroundColor: `${colors.primary[600]}15` }}>
                <ActivityIndicator size="small" color={colors.primary[600]} />
                <Text style={{ color: colors.primary[600] }} className="text-xs">Indexing documents for AI...</Text>
              </View>
            )}

            {/* Missing Documents Alert */}
            {missingDocuments.length > 0 && documents.length > 0 && (
              <View className="mx-4 mt-4 p-3 rounded-xl" style={{ backgroundColor: colors.amber.light }}>
                <View className="flex-row items-center gap-2">
                  <AlertTriangle size={16} color={colors.warning} />
                  <Text style={{ color: colors.warning }} className="font-medium text-sm">{missingDocuments.length} missing documents</Text>
                </View>
                <Text style={{ color: colors.text.secondary }} className="text-xs mt-1">
                  {missingDocuments.map(d => d.label).join(', ')}
                </Text>
              </View>
            )}

            {/* Documents List */}
            <ScrollView className="flex-1 p-4">
              {filteredDocs.map((doc, index) => {
                const status = statusConfig[doc.status] ?? statusFallback;
                const { icon: TypeIcon, color: typeColor } = getFileIcon(doc.mimeType, colors);
                const isSelected = selectedDoc?.id === doc.id;

                return (
                  <Animated.View key={doc.id} entering={FadeIn.delay(index * 50)}>
                    <AnimatedPressable
                      onPress={() => setSelectedDoc(isSelected ? null : doc)}
                      className="rounded-xl p-4 mb-3"
                      style={{
                        backgroundColor: isSelected ? `${colors.primary[600]}10` : colors.surface.secondary,
                        borderWidth: isSelected ? 2 : 0,
                        borderColor: colors.primary[600],
                      }}
                    >
                      <View className="flex-row items-start gap-3">
                        <View className="w-10 h-10 rounded-lg items-center justify-center" style={{ backgroundColor: `${typeColor}15` }}>
                          <TypeIcon size={20} color={typeColor} />
                        </View>
                        <View className="flex-1">
                          <Text style={{ color: colors.text.primary }} className="font-medium mb-1" numberOfLines={1}>{doc.name}</Text>
                          <View className="flex-row items-center gap-2">
                            <Text style={{ color: colors.text.tertiary }} className="text-xs">{doc.size}</Text>
                            {doc.uploadDate && (
                              <>
                                <Text style={{ color: colors.text.tertiary }} className="text-xs">•</Text>
                                <Text style={{ color: colors.text.tertiary }} className="text-xs">{doc.uploadDate}</Text>
                              </>
                            )}
                          </View>
                        </View>
                        <View className="px-2 py-1 rounded-full" style={{ backgroundColor: status.bg }}>
                          <Text style={{ color: status.color }} className="text-xs font-medium">{status.label}</Text>
                        </View>
                      </View>

                      {/* Actions */}
                      <View className="flex-row gap-2 mt-3">
                        <AnimatedPressable onPress={() => handleOpenDocument(doc)} className="flex-1 flex-row items-center justify-center gap-1.5 py-2 rounded-lg" style={{ backgroundColor: colors.surface.primary }}>
                          <ExternalLink size={14} color={colors.text.secondary} />
                          <Text style={{ color: colors.text.secondary }} className="text-xs font-medium">Open</Text>
                        </AnimatedPressable>
                        <AnimatedPressable onPress={() => updateDocStatus(doc.id, 'reviewed')} className="flex-row items-center justify-center gap-1.5 py-2 px-3 rounded-lg" style={{ backgroundColor: doc.status === 'reviewed' ? colors.traffic.greenLight : colors.surface.primary }}>
                          <Check size={14} color={doc.status === 'reviewed' ? colors.success : colors.text.secondary} />
                        </AnimatedPressable>
                        <AnimatedPressable onPress={() => updateDocStatus(doc.id, 'flagged')} className="flex-row items-center justify-center gap-1.5 py-2 px-3 rounded-lg" style={{ backgroundColor: doc.status === 'flagged' ? colors.red.light : colors.surface.primary }}>
                          <Flag size={14} color={doc.status === 'flagged' ? colors.error : colors.text.secondary} />
                        </AnimatedPressable>
                      </View>
                    </AnimatedPressable>
                  </Animated.View>
                );
              })}

              {/* Empty State */}
              {!isLoadingDocs && !docsError && documents.length === 0 && documentsOpen && (
                <View className="items-center justify-center py-8">
                  <FileText size={40} color={colors.text.tertiary} />
                  <Text style={{ color: colors.text.secondary }} className="mt-2 font-medium">No documents found</Text>
                  <Text style={{ color: colors.text.tertiary }} className="text-sm text-center mt-1">
                    The connected data room folder is empty.
                  </Text>
                </View>
              )}
            </ScrollView>
          </Animated.View>

          {/* Right Panel - AI Chat / Collaborate */}
          <Animated.View
            entering={FadeInRight.delay(200).duration(400)}
            className="flex-1 flex-col"
            style={{ backgroundColor: colors.surface.secondary }}
          >
            {/* Tab Switcher */}
            <View className="flex-row" style={{ backgroundColor: colors.surface.primary, borderBottomWidth: 1, borderBottomColor: colors.border.light }}>
              <Pressable
                onPress={() => setDesktopRightTab('chat')}
                className="flex-1 flex-row items-center justify-center gap-2 py-4"
                style={{ borderBottomWidth: 2, borderBottomColor: desktopRightTab === 'chat' ? colors.primary[600] : 'transparent' }}
              >
                <Sparkles size={18} color={desktopRightTab === 'chat' ? colors.primary[600] : colors.text.tertiary} />
                <Text style={{ color: desktopRightTab === 'chat' ? colors.primary[600] : colors.text.tertiary }} className="font-medium">AI Assistant</Text>
              </Pressable>
              <Pressable
                onPress={() => setDesktopRightTab('collaborate')}
                className="flex-1 flex-row items-center justify-center gap-2 py-4"
                style={{ borderBottomWidth: 2, borderBottomColor: desktopRightTab === 'collaborate' ? colors.primary[600] : 'transparent' }}
              >
                <Users size={18} color={desktopRightTab === 'collaborate' ? colors.primary[600] : colors.text.tertiary} />
                <Text style={{ color: desktopRightTab === 'collaborate' ? colors.primary[600] : colors.text.tertiary }} className="font-medium">Collaborate</Text>
              </Pressable>
            </View>

            {desktopRightTab === 'chat' ? (
              // AI Chat - Real DDChatContainer
              <View className="flex-1">
                <DDChatContainer
                  startupContext={startupContext}
                  startupId={startup.startup.id}
                  flexLayout={true}
                  investorPreferences={{
                    focusAreas: investorProfile?.focusAreas,
                    riskTolerance: investorProfile?.riskTolerance,
                    investmentStage: investorProfile?.investmentStages,
                  }}
                />
              </View>
            ) : (
              // Collaborate tab content
              <ScrollView className="flex-1 p-6">
                <SharedDDPanel
                  startupId={startup.id}
                  startupName={startup.startup.company?.name}
                  investorUserId={user?.id}
                  investorName={user?.name || 'Investor'}
                  isPremium={isPremium}
                  availableDocuments={availableDocuments}
                  onDocumentPress={handleDocumentPress}
                />
              </ScrollView>
            )}
          </Animated.View>

          {/* Conclusion Side Panel */}
          {showConclusionPanel && (
            <Animated.View
              entering={SlideInRight.springify().damping(20)}
              className="w-[380px] border-l"
              style={{ backgroundColor: colors.surface.primary, borderLeftColor: colors.border.light }}
            >
              <View className="flex-row items-center justify-between p-4 border-b" style={{ borderBottomColor: colors.border.light }}>
                <Text style={{ color: colors.text.primary }} className="text-lg font-bold">Close Due Diligence</Text>
                <Pressable onPress={() => setShowConclusionPanel(false)} className="w-8 h-8 rounded-full items-center justify-center" style={{ backgroundColor: colors.surface.secondary }}>
                  <XCircle size={18} color={colors.text.secondary} />
                </Pressable>
              </View>

              <KeyboardAwareBody className="flex-1 p-4">
                <Text style={{ color: colors.text.secondary }} className="mb-4">Select your investment decision for {startup.startup.company?.name}:</Text>

                <View className="gap-3 mb-6">
                  {[
                    { key: 'invest', label: 'Invest', desc: 'Proceed with investment', icon: ThumbsUp, color: colors.success, bg: colors.traffic.greenLight },
                    { key: 'follow_up', label: 'Follow Up', desc: 'Need more info/time', icon: Clock, color: colors.warning, bg: colors.amber.light },
                    { key: 'pass', label: 'Pass', desc: 'Not proceeding', icon: ThumbsDown, color: colors.error, bg: colors.red.light },
                  ].map((opt) => (
                    <AnimatedPressable
                      key={opt.key}
                      onPress={() => setDecision(opt.key as DecisionType)}
                      className="flex-row items-center gap-4 p-4 rounded-xl"
                      style={{ backgroundColor: decision === opt.key ? opt.bg : colors.surface.secondary, borderWidth: decision === opt.key ? 2 : 0, borderColor: opt.color }}
                    >
                      <View className="w-12 h-12 rounded-full items-center justify-center" style={{ backgroundColor: opt.bg }}>
                        <opt.icon size={24} color={opt.color} />
                      </View>
                      <View className="flex-1">
                        <Text style={{ color: colors.text.primary }} className="font-semibold">{opt.label}</Text>
                        <Text style={{ color: colors.text.tertiary }} className="text-xs">{opt.desc}</Text>
                      </View>
                      {decision === opt.key && <CheckCircle size={20} color={opt.color} />}
                    </AnimatedPressable>
                  ))}
                </View>

                <Text style={{ color: colors.text.secondary }} className="mb-2">Final Comments:</Text>
                <TextInput
                  className="rounded-xl p-4 text-sm mb-4"
                  style={{ backgroundColor: colors.surface.secondary, color: colors.text.primary, minHeight: 120 }}
                  placeholder="Share your feedback and reasoning..."
                  placeholderTextColor={colors.text.tertiary}
                  value={finalComments}
                  onChangeText={setFinalComments}
                  multiline
                  textAlignVertical="top"
                />

                <AnimatedPressable
                  onPress={handleSubmitConclusion}
                  disabled={!decision || !finalComments.trim()}
                  className="py-4 rounded-xl items-center"
                  style={{ backgroundColor: decision && finalComments.trim() ? colors.primary[600] : colors.surface.tertiary }}
                >
                  <Text className="font-semibold" style={{ color: decision && finalComments.trim() ? 'white' : colors.text.tertiary }}>Submit Conclusion</Text>
                </AnimatedPressable>

                <Text style={{ color: colors.text.tertiary }} className="text-xs text-center mt-3">
                  The startup can choose to share this with other investors
                </Text>
              </KeyboardAwareBody>
            </Animated.View>
          )}

          {/* Soft Commitment Side Panel (Desktop) */}
          {showCommitmentPanel && (
            <Animated.View
              entering={SlideInRight.springify().damping(20)}
              className="w-[380px] border-l"
              style={{ backgroundColor: colors.surface.primary, borderLeftColor: colors.border.light }}
            >
              <View className="flex-row items-center justify-between p-4 border-b" style={{ borderBottomColor: colors.border.light }}>
                <View className="flex-row items-center gap-2">
                  <HandCoins size={20} color={colors.primary[600]} />
                  <Text style={{ color: colors.text.primary }} className="text-lg font-bold">Signal Interest</Text>
                </View>
                <Pressable onPress={() => setShowCommitmentPanel(false)} className="w-8 h-8 rounded-full items-center justify-center" style={{ backgroundColor: colors.surface.secondary }}>
                  <XCircle size={18} color={colors.text.secondary} />
                </Pressable>
              </View>

              <KeyboardAwareBody className="flex-1 p-4">
                <Text style={{ color: colors.text.secondary }} className="mb-6">
                  Indicate your investment interest in {startup.startup.company?.name}. This is a soft commitment that you can adjust at any time.
                </Text>

                {/* Amount Input */}
                <Text style={{ color: colors.text.secondary }} className="text-sm font-medium mb-2">Commitment Amount</Text>
                <View className="flex-row items-center gap-2 mb-6">
                  <View className="flex-row items-center flex-1 rounded-xl px-4" style={{ backgroundColor: colors.surface.secondary, borderWidth: 1, borderColor: colors.border.light }}>
                    <Text style={{ color: colors.text.primary }} className="text-xl font-semibold mr-1">$</Text>
                    <TextInput
                      className="flex-1 py-3.5 text-xl"
                      style={{ color: colors.text.primary }}
                      value={commitmentAmount}
                      onChangeText={setCommitmentAmount}
                      keyboardType="numeric"
                      placeholder="0"
                      placeholderTextColor={colors.text.tertiary}
                    />
                    <Text style={{ color: colors.text.tertiary }} className="text-lg font-medium">K</Text>
                  </View>
                </View>

                {/* Notes Input */}
                <Text style={{ color: colors.text.secondary }} className="text-sm font-medium mb-2">Notes (optional)</Text>
                <TextInput
                  className="rounded-xl p-4 text-sm mb-6"
                  style={{ backgroundColor: colors.surface.secondary, color: colors.text.primary, minHeight: 100, borderWidth: 1, borderColor: colors.border.light }}
                  placeholder="Any conditions, timeline preferences, or comments..."
                  placeholderTextColor={colors.text.tertiary}
                  value={commitmentNotes}
                  onChangeText={setCommitmentNotes}
                  multiline
                  textAlignVertical="top"
                />

                {/* Disclaimer */}
                <View className="rounded-lg p-3 mb-6" style={{ backgroundColor: colors.amber.light }}>
                  <Text style={{ color: colors.warning }} className="text-xs leading-4">
                    Soft commitment during active DD. You can adjust or withdraw anytime.
                  </Text>
                </View>

                <AnimatedPressable
                  onPress={handleSubmitSoftCommit}
                  disabled={!commitmentAmount || parseFloat(commitmentAmount) <= 0}
                  className="py-4 rounded-xl items-center"
                  style={{ backgroundColor: commitmentAmount && parseFloat(commitmentAmount) > 0 ? colors.primary[600] : colors.surface.tertiary }}
                >
                  <Text className="font-semibold" style={{ color: commitmentAmount && parseFloat(commitmentAmount) > 0 ? 'white' : colors.text.tertiary }}>
                    Commit {commitmentAmount && parseFloat(commitmentAmount) > 0 ? formatCurrency(parseFloat(commitmentAmount) * 1000) : ''}
                  </Text>
                </AnimatedPressable>
              </KeyboardAwareBody>
            </Animated.View>
          )}

          {/* Hard Commit Side Panel (Desktop - after DD close invest) */}
          {showHardCommitPanel && (
            <Animated.View
              entering={SlideInRight.springify().damping(20)}
              className="w-[380px] border-l"
              style={{ backgroundColor: colors.surface.primary, borderLeftColor: colors.border.light }}
            >
              <View className="flex-row items-center justify-between p-4 border-b" style={{ borderBottomColor: colors.border.light }}>
                <View className="flex-row items-center gap-2">
                  <DollarSign size={20} color={colors.success} />
                  <Text style={{ color: colors.text.primary }} className="text-lg font-bold">
                    {activeCommitment?.type === 'soft' ? 'Confirm Investment' : 'Investment Commitment'}
                  </Text>
                </View>
                <Pressable onPress={() => setShowHardCommitPanel(false)} className="w-8 h-8 rounded-full items-center justify-center" style={{ backgroundColor: colors.surface.secondary }}>
                  <XCircle size={18} color={colors.text.secondary} />
                </Pressable>
              </View>

              <KeyboardAwareBody className="flex-1 p-4">
                <Text style={{ color: colors.text.secondary }} className="mb-4">
                  {activeCommitment?.type === 'soft'
                    ? `Convert your soft commitment to a firm investment in ${startup.startup.company?.name}.`
                    : `Enter your investment commitment for ${startup.startup.company?.name}.`}
                </Text>

                {activeCommitment?.type === 'soft' && (
                  <View className="flex-row items-center gap-2 mb-6 px-4 py-3 rounded-xl" style={{ backgroundColor: `${colors.primary[600]}10` }}>
                    <TrendingUp size={16} color={colors.primary[600]} />
                    <Text style={{ color: colors.primary[600] }} className="text-sm font-medium">
                      Previous soft commitment: {formatCurrency(activeCommitment.amount)}
                    </Text>
                  </View>
                )}

                <Text style={{ color: colors.text.secondary }} className="text-sm font-medium mb-2">Final Amount</Text>
                <View className="flex-row items-center gap-2 mb-6">
                  <View className="flex-row items-center flex-1 rounded-xl px-4" style={{ backgroundColor: colors.surface.secondary, borderWidth: 2, borderColor: colors.success }}>
                    <Text style={{ color: colors.text.primary }} className="text-xl font-semibold mr-1">$</Text>
                    <TextInput
                      className="flex-1 py-3.5 text-xl"
                      style={{ color: colors.text.primary }}
                      value={hardCommitAmount}
                      onChangeText={setHardCommitAmount}
                      keyboardType="numeric"
                      placeholder="0"
                      placeholderTextColor={colors.text.tertiary}
                    />
                    <Text style={{ color: colors.text.tertiary }} className="text-lg font-medium">K</Text>
                  </View>
                </View>

                <View className="flex-row gap-3">
                  <Pressable
                    onPress={() => setShowHardCommitPanel(false)}
                    className="flex-1 py-4 rounded-xl items-center active:opacity-90"
                    style={{ backgroundColor: colors.surface.secondary }}
                  >
                    <Text className="font-medium" style={{ color: colors.text.secondary }}>Skip</Text>
                  </Pressable>
                  <AnimatedPressable
                    onPress={handleSubmitHardCommit}
                    disabled={!hardCommitAmount || parseFloat(hardCommitAmount) <= 0}
                    className="flex-1 py-4 rounded-xl items-center"
                    style={{ backgroundColor: hardCommitAmount && parseFloat(hardCommitAmount) > 0 ? colors.success : colors.surface.tertiary }}
                  >
                    <Text className="font-semibold" style={{ color: hardCommitAmount && parseFloat(hardCommitAmount) > 0 ? 'white' : colors.text.tertiary }}>
                      Confirm Investment
                    </Text>
                  </AnimatedPressable>
                </View>

                <Text style={{ color: colors.text.tertiary }} className="text-xs text-center mt-4">
                  This will be recorded as a verified hard commitment.
                </Text>
              </KeyboardAwareBody>
            </Animated.View>
          )}
        </View>
      </View>
    </AppLayout>
  );
}
